import { http } from '../lib/api/http';
import { createNotification } from './notificationsApi';

const SUPPORT_TEAM_COMPANY_ID =
  (typeof process !== 'undefined' &&
    process.env &&
    process.env.REACT_APP_SUPPORT_TEAM_COMPANY_ID) ||
  '';

const STATUS_LABELS = {
  open: 'Abierto',
  in_progress: 'En progreso',
  resolved: 'Resuelto',
};

const resolveStatusLabel = value => STATUS_LABELS[value] || value || 'sin estado';

const notifySupportTeamTicketCreated = async (
  ticket,
  { reporterName, reporterEmail, supportCompanyId, createdById } = {}
) => {
  const companyId = supportCompanyId || SUPPORT_TEAM_COMPANY_ID;
  if (!companyId) {
    console.warn('No se configuró una empresa de soporte para recibir notificaciones de tickets.');
    return;
  }

  const normalizedReporterName = (reporterName || '').trim();
  const normalizedReporterEmail = (reporterEmail || '').trim();
  const reporterLabel =
    normalizedReporterName ||
    normalizedReporterEmail ||
    ticket.reporter?.name ||
    ticket.reporter?.email ||
    'Un usuario';

  const messagePieces = [
    `${reporterLabel} registró el ticket "${ticket.subject || 'sin asunto'}".`,
  ];
  const companyName = ticket.company?.name;
  if (companyName) {
    messagePieces.push(`Empresa: ${companyName}.`);
  }

  await createNotification({
    type: 'support_ticket_created',
    title: 'Nuevo ticket de soporte',
    message: messagePieces.join(' '),
    companyId,
    destinationUrl: '/admin?tab=support',
    createdById: createdById || ticket.reporterId || undefined,
    data: {
      ticketId: ticket.id,
      ticketStatus: ticket.status,
      ticketSubject: ticket.subject,
      ticketCompanyId: ticket.companyId || '',
    },
  });
};

const notifyReporterAboutStatusChange = async (
  ticket,
  previousStatus,
  { updatedById, companyIdFallback } = {}
) => {
  const companyId = ticket.companyId || companyIdFallback;
  if (!companyId) {
    return;
  }

  const previousLabel = resolveStatusLabel(previousStatus);
  const nextLabel = resolveStatusLabel(ticket.status);
  const subject = ticket.subject || 'Ticket de soporte';

  const notificationData = {
    ticketId: ticket.id,
    previousStatus,
    newStatus: ticket.status,
  };
  if (ticket.reporterId) {
    notificationData.targetUserId = ticket.reporterId;
  }

  await createNotification({
    type: 'support_ticket_status',
    title: 'Actualización de tu ticket de soporte',
    message: `Tu ticket "${subject}" cambió de ${previousLabel} a ${nextLabel}.`,
    companyId,
    destinationUrl: `/dashboard?section=support-tickets&ticket=${ticket.id}`,
    createdById: updatedById || ticket.resolvedById || undefined,
    data: notificationData,
  });
};

const mapUser = input => {
  if (!input) {
    return {
      id: '',
      name: '',
      email: '',
      role: '',
    };
  }

  return {
    id: input.id || '',
    name: input.name || input.email || 'Usuario sin nombre',
    email: input.email || '',
    role: input.role || '',
  };
};

const mapCompany = input => {
  if (!input) {
    return {
      id: '',
      name: '',
      slug: '',
    };
  }

  return {
    id: input.id || '',
    name: input.name || '',
    slug: input.slug || '',
  };
};

const normalizeCategory = value => {
  const allowed = ['general', 'billing', 'technical', 'incident', 'improvement'];
  if (allowed.includes(value)) return value;
  return 'general';
};

const normalizeTicketType = value => {
  return value === 'bug' ? 'bug' : 'support';
};

const normalizePriority = value => {
  const allowed = ['low', 'medium', 'high'];
  if (allowed.includes(value)) return value;
  return '';
};

export const mapSupportTicketRecord = record => ({
  id: record.id,
  subject: record.subject || '',
  description: record.description || '',
  category: normalizeCategory(record.category),
  ticketType: normalizeTicketType(record.ticketType || record.ticket_type),
  status: record.status || 'open',
  priority: normalizePriority(record.priority),
  companyId: record.companyId || record.company_id || '',
  reporterId: record.reporterId || record.reporter_id || '',
  resolvedById:
    record.resolvedById || record.resolved_by || record.resolvedBy || '',
  resolutionNotes: record.resolutionNotes || record.resolution_notes || '',
  resolvedAt: record.resolvedAt || record.resolved_at || '',
  created: record.createdAt || record.created_at || record.created,
  updated: record.updatedAt || record.updated_at || record.updated,
  reporter: mapUser(record.reporter),
  resolvedBy: mapUser(record.resolvedByUser || record.resolved_by_user),
  company: mapCompany(record.company),
});

const buildCreatePayload = payload => {
  if (!payload) {
    throw new Error('No se proporcionaron datos del ticket.');
  }

  const body = {
    subject: payload.subject?.trim() || '',
    description: payload.description?.trim() || '',
    ticket_type: normalizeTicketType(payload.ticketType),
    status: payload.status || 'open',
  };

  if (payload.category) {
    body.category = normalizeCategory(payload.category);
  }

  if (payload.priority) {
    const priority = normalizePriority(payload.priority);
    if (priority) {
      body.priority = priority;
    }
  }

  if (payload.companyId) {
    body.company_id = payload.companyId;
  }

  return body;
};

const buildUpdatePayload = payload => {
  const body = {};
  if (payload.subject !== undefined) {
    body.subject = payload.subject?.trim() || '';
  }
  if (payload.description !== undefined) {
    body.description = payload.description?.trim() || '';
  }
  if (payload.category !== undefined) {
    body.category = normalizeCategory(payload.category);
  }
  if (payload.ticketType !== undefined) {
    body.ticket_type = normalizeTicketType(payload.ticketType);
  }
  if (payload.status !== undefined) {
    body.status = payload.status || 'open';
  }
  if (payload.priority !== undefined) {
    const priority = normalizePriority(payload.priority);
    body.priority = priority || '';
  }
  if (payload.companyId !== undefined) {
    body.company_id = payload.companyId || '';
  }
  if (payload.resolvedById !== undefined) {
    body.resolved_by = payload.resolvedById || '';
  }
  if (payload.resolutionNotes !== undefined) {
    body.resolution_notes = payload.resolutionNotes?.trim() || '';
  }
  if (payload.resolvedAt !== undefined) {
    body.resolved_at = payload.resolvedAt || '';
  }

  return body;
};

export async function submitSupportTicket(payload) {
  const response = await http.post('/support-tickets', buildCreatePayload(payload));
  const ticket = mapSupportTicketRecord(response.data || {});

  notifySupportTeamTicketCreated(ticket, {
    reporterName: payload?.reporterName,
    reporterEmail: payload?.reporterEmail,
    supportCompanyId: payload?.supportCompanyId,
    createdById: payload?.reporterId,
  }).catch(error => {
    console.warn('No se pudo notificar al equipo de soporte sobre el nuevo ticket.', error);
  });

  return ticket;
}

export async function listSupportTickets({
  reporterId,
  companyId,
  includeCompanyTickets = false,
  status,
  ticketType,
  search = '',
  page = 1,
  perPage = 50,
  sort = 'created_at DESC',
} = {}) {
  const params = {
    page,
    per_page: perPage,
    sort,
  };
  if (reporterId) {
    params.reporter_id = reporterId;
  }
  if (companyId) {
    params.company_id = companyId;
  }
  if (includeCompanyTickets) {
    params.include_company = true;
  }
  if (status && status !== 'all') {
    params.status = status;
  }
  if (ticketType && ticketType !== 'all') {
    params.ticket_type = ticketType;
  }
  if (search?.trim()) {
    params.search = search.trim();
  }

  const response = await http.get('/support-tickets', { params });
  const payload = response.data || {};
  const items = Array.isArray(payload.items) ? payload.items : [];

  return {
    items: items.map(mapSupportTicketRecord),
    page: payload.page || page,
    perPage: payload.per_page || perPage,
    totalItems: payload.total || items.length,
    totalPages: payload.total_pages || 1,
  };
}

export async function getSupportTicketById(id) {
  if (!id) {
    throw new Error('Se requiere el identificador del ticket.');
  }

  const response = await http.get(`/support-tickets/${encodeURIComponent(id)}`);
  return mapSupportTicketRecord(response.data || {});
}

export async function updateSupportTicket(id, payload) {
  if (!id) {
    throw new Error('Se requiere el identificador del ticket.');
  }

  const checkStatusChange = payload && Object.prototype.hasOwnProperty.call(payload, 'status');
  let previousTicket = null;

  if (checkStatusChange) {
    try {
      previousTicket = await getSupportTicketById(id);
    } catch (error) {
      if (error?.status !== 404) {
        console.warn('No se pudo obtener el ticket antes de actualizarlo.', error);
      }
    }
  }

  const response = await http.patch(
    `/support-tickets/${encodeURIComponent(id)}`,
    buildUpdatePayload(payload)
  );
  const ticket = mapSupportTicketRecord(response.data || {});

  if (checkStatusChange && previousTicket) {
    const previousStatus = previousTicket.status;
    if (previousStatus && previousStatus !== ticket.status) {
      notifyReporterAboutStatusChange(ticket, previousStatus, {
        updatedById: payload?.resolvedById,
        companyIdFallback: previousTicket.companyId || payload?.companyId,
      }).catch(error => {
        console.warn('No se pudo notificar el cambio de estado del ticket.', error);
      });
    }
  }

  return ticket;
}

export async function resolveSupportTicket(
  id,
  { resolutionNotes = '', resolvedById, status = 'resolved' } = {}
) {
  const resolvedAt = status === 'resolved' ? new Date().toISOString() : '';
  return updateSupportTicket(id, {
    resolutionNotes,
    resolvedById,
    resolvedAt,
    status,
  });
}

export async function reopenSupportTicket(id) {
  return updateSupportTicket(id, {
    status: 'open',
    resolutionNotes: '',
    resolvedAt: '',
    resolvedById: '',
  });
}
